import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useLanguage, LangToggle } from '../context/LanguageContext'
import { useSubscription } from '../context/SubscriptionContext'

export default function AddWarranty() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const { canAddWarranty } = useSubscription()
  const { id: editId } = useParams()
  const isEdit = Boolean(editId)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(isEdit)
  const [error, setError] = useState('')
  const [pdfFile, setPdfFile] = useState(null)
  const [existingPdfUrl, setExistingPdfUrl] = useState(null)
  const [uploadProgress, setUploadProgress] = useState('')
  const [scanMode, setScanMode] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [scanPreview, setScanPreview] = useState(null)
  const [scanResult, setScanResult] = useState(null)
  const cameraInputRef = useRef(null)
  const galleryInputRef = useRef(null)

  const [form, setForm] = useState({
    product_name: '', brand: '', category: t.defaultCategory,
    purchase_date: '', expiry_date: '', store: '', serial_number: '', notes: '',
  })

  useEffect(() => {
    if (!isEdit) return
    supabase.from('warranties').select('*').eq('id', editId).single().then(({ data }) => {
      if (data) {
        setForm({
          product_name: data.product_name || '',
          brand: data.brand || '',
          category: data.category || t.defaultCategory,
          purchase_date: data.purchase_date || '',
          expiry_date: data.expiry_date || '',
          store: data.store || '',
          serial_number: data.serial_number || '',
          notes: data.notes || '',
        })
        if (data.image_url) setExistingPdfUrl(data.image_url)
      }
      setLoadingData(false)
    })
  }, [editId, isEdit])

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }))

  const handleScanImage = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    e.target.value = ''
    setScanMode(true); setScanning(true); setScanResult(null); setError('')
    const reader = new FileReader()
    reader.onload = (ev) => setScanPreview(ev.target.result)
    reader.readAsDataURL(file)
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader()
        r.onload = () => res(r.result.split(',')[1])
        r.onerror = () => rej(new Error('Read failed'))
        r.readAsDataURL(file)
      })
      const response = await fetch('/api/scan', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base64 })
      })
      if (!response.ok) throw new Error('Scan failed')
      setScanResult(await response.json())
      setScanning(false)
    } catch {
      setScanning(false); setError(t.scanFailed); setScanMode(false)
    }
  }

  const applyScannedData = () => {
    if (!scanResult) return
    setForm(f => ({
      ...f,
      product_name:  scanResult.product_name  || f.product_name,
      brand:         scanResult.brand         || f.brand,
      category:      scanResult.category      || f.category,
      purchase_date: scanResult.purchase_date || f.purchase_date,
      expiry_date:   scanResult.expiry_date   || f.expiry_date,
      store:         scanResult.store         || f.store,
      serial_number: scanResult.serial_number || f.serial_number,
      notes:         scanResult.notes         || f.notes,
    }))
    setScanMode(false); setScanPreview(null); setScanResult(null)
  }

  const cancelScan = () => { setScanMode(false); setScanPreview(null); setScanResult(null); setScanning(false) }
  const retryScan  = () => { setScanResult(null); setScanPreview(null); setScanning(false); setScanMode(false); setTimeout(() => galleryInputRef.current.click(), 100) }

  const handlePdfChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.type !== 'application/pdf') { setError(t.pdfOnly); return }
    if (file.size > 10 * 1024 * 1024) { setError(t.pdfTooLarge); return }
    setError(''); setPdfFile(file)
  }

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('')
    let pdf_url = isEdit ? existingPdfUrl : null
    if (pdfFile) {
      setUploadProgress(t.uploadingFile)
      const fileName = `${user.id}/${Date.now()}_${pdfFile.name}`
      const { error: uploadError } = await supabase.storage.from('Warranties').upload(fileName, pdfFile)
      if (uploadError) { setError(t.uploadFailed + uploadError.message); setLoading(false); setUploadProgress(''); return }
      const { data: { publicUrl } } = supabase.storage.from('Warranties').getPublicUrl(fileName)
      pdf_url = publicUrl; setUploadProgress('')
    }
    if (isEdit) {
      const { error } = await supabase.from('warranties').update({ ...form, image_url: pdf_url }).eq('id', editId)
      if (error) { setError(t.saveError); setLoading(false); return }
      navigate(`/warranty/${editId}`)
    } else {
      const { error } = await supabase.from('warranties').insert({ ...form, user_id: user.id, image_url: pdf_url })
      if (error) { setError(t.saveError); setLoading(false); return }
      navigate('/dashboard')
    }
  }

  return (
    <div className="app-layout">
      <div className="topbar">
        <button className="topbar-btn" onClick={() => navigate('/dashboard')} aria-label="Back">{t.dir === 'rtl' ? '→' : '←'}</button>
        <div className="topbar-logo"><span style={{ color: '#E8610A', fontWeight: 900, fontSize: '1rem' }}>{isEdit ? (t.editWarranty || 'Edit Warranty') : t.addWarrantyTitle}</span></div>
        <LangToggle />
      </div>

      <div className="main-content">
        {!canAddWarranty && !isEdit && (
          <div className="upgrade-banner">
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔒</div>
            <div className="upgrade-banner-title">{t.warrantyLimitReached}</div>
            <div className="upgrade-banner-sub">{t.warrantyLimitSub}</div>
            <button className="upgrade-banner-btn" onClick={() => navigate('/pricing')}>{t.upgradeNow}</button>
          </div>
        )}

        {(canAddWarranty || isEdit) && error && (
          <div className="error-msg" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{error}</span>
            <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#ff8080', cursor: 'pointer', fontSize: '1.1rem', padding: '0 0 0 8px' }}>✕</button>
          </div>
        )}

        {(canAddWarranty || isEdit) && scanMode && (
          <div className="scan-overlay">
            {scanning ? (
              <div className="scan-loading">
                <div className="scan-spinner" />
                <div className="scan-loading-text">{t.scanAnalyzing}</div>
                <div className="scan-loading-sub">{t.scanAutoFill}</div>
                {scanPreview && <img src={scanPreview} alt="preview" className="scan-preview-img" />}
              </div>
            ) : scanResult && (
              <div className="scan-result">
                <div className="scan-result-title">{t.scanExtracted}</div>
                <div className="scan-result-subtitle">{t.scanReview}</div>
                {scanPreview && <img src={scanPreview} alt="preview" className="scan-preview-img" />}
                <div className="scan-fields">
                  {Object.entries(t.scanFieldLabels).map(([key, label]) =>
                    scanResult[key] ? (
                      <div key={key} className="scan-field">
                        <span className="scan-field-label">{label}</span>
                        <span className="scan-field-value">{scanResult[key]}</span>
                      </div>
                    ) : null
                  )}
                </div>
                <div className="scan-actions">
                  <button className="btn-full" onClick={applyScannedData}>{t.confirmFill}</button>
                  <button className="btn-full" style={{ marginTop: 8, background: '#2a2a2a', border: '1px solid #3a3a3a' }} onClick={retryScan}>{t.rescan}</button>
                  <button className="delete-btn" style={{ marginTop: 8 }} onClick={cancelScan}>{t.cancelScan}</button>
                </div>
              </div>
            )}
          </div>
        )}

        {(canAddWarranty || isEdit) && !scanMode && !isEdit && (
          <div className="scan-section">
            <div className="scan-section-title">{t.scanTitle}</div>
            <div className="scan-section-sub">{t.scanSub}</div>
            <div className="scan-btns">
              <button className="scan-btn" onClick={() => cameraInputRef.current.click()}>
                <span style={{ fontSize: '1.6rem' }}>📷</span><span>{t.camera}</span>
              </button>
              <button className="scan-btn" onClick={() => galleryInputRef.current.click()}>
                <span style={{ fontSize: '1.6rem' }}>🖼️</span><span>{t.fromGallery}</span>
              </button>
            </div>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleScanImage} style={{ display: 'none' }} />
            <input ref={galleryInputRef} type="file" accept="image/*" onChange={handleScanImage} style={{ display: 'none' }} />
            <div className="scan-divider"><span>{t.orManually}</span></div>
          </div>
        )}

        {(canAddWarranty || isEdit) && !scanMode && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label" htmlFor="add-product">{t.productName} *</label>
              <input className="form-input" id="add-product" placeholder={t.productPlaceholder} value={form.product_name} onChange={e => set('product_name', e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-brand">{t.brand}</label>
              <input className="form-input" id="add-brand" placeholder={t.brandPlaceholder} value={form.brand} onChange={e => set('brand', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t.category}</label>
              <div className="category-grid">
                {t.categories.map(cat => (
                  <button key={cat.label} type="button"
                    className={`cat-btn ${form.category === cat.label ? 'active' : ''}`}
                    onClick={() => set('category', cat.label)}>
                    <span className="cat-icon">{cat.icon}</span>
                    <span>{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-purchase-date">{t.purchaseDate} *</label>
              <input className="form-input" id="add-purchase-date" type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-expiry-date">{t.expiryDate} *</label>
              <input className="form-input" id="add-expiry-date" type="date" value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} required min={form.purchase_date || undefined} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-store">{t.store}</label>
              <input className="form-input" id="add-store" placeholder={t.storePlaceholder} value={form.store} onChange={e => set('store', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-serial">{t.serialNumber}</label>
              <input className="form-input" id="add-serial" placeholder={t.optional} value={form.serial_number} onChange={e => set('serial_number', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t.warrantyDoc}</label>
              <label className="pdf-upload-area" htmlFor="pdf-input">
                {pdfFile ? (
                  <><span style={{ fontSize: '1.8rem' }}>📄</span>
                    <span className="pdf-upload-name">{pdfFile.name}</span>
                    <span className="pdf-upload-size">{(pdfFile.size / 1024).toFixed(0)} KB</span>
                    <span className="pdf-upload-change">{t.changePdf}</span></>
                ) : (
                  <><span style={{ fontSize: '1.8rem' }}>📎</span>
                    <span className="pdf-upload-label">{t.uploadPdfLabel}</span>
                    <span className="pdf-upload-sub">{t.uploadPdfSub}</span></>
                )}
              </label>
              <input id="pdf-input" type="file" accept="application/pdf" onChange={handlePdfChange} style={{ display: 'none' }} />
              {uploadProgress && <div style={{ color: 'var(--orange)', fontSize: '0.82rem', marginTop: '8px', textAlign: 'center' }}>{uploadProgress}</div>}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="add-notes">{t.notes}</label>
              <textarea className="form-input" id="add-notes" rows={3} placeholder={t.notesPlaceholder} value={form.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'vertical' }} />
            </div>
            <button className="btn-full" type="submit" disabled={loading || loadingData}>
              {loading ? (uploadProgress || t.saving) : isEdit ? (t.updateWarranty || 'Save Changes') : t.saveWarranty}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
